import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Modal, Pressable, ScrollView, View } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CompactActionButton, IconButton } from "@louise/moon-ui";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { X } from "lucide-react-native";

import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useCountryCode, useLocale, useTranslations } from "@src/packages/i18n";

import {
  CoachRecordsProvider,
  useCoachRecords,
} from "../records/useCoachRecords";
import { resolveCoachProtocols } from "../availability";
import { deviceDecision } from "../deviceDecision";
import { presentationDecision } from "../presentationDecision";
import { boxDecision } from "../boxDecision";
import {
  deviceList,
  recordedDeviceLabel,
  methodIsOffered,
  methodsFor,
  protocolsForMethod,
} from "../deviceChoices";
import { InjectionCoachConsentSheet } from "./InjectionCoachConsentSheet";
import {
  Card,
  CoachCardSurfaceProvider,
  DeviceConfirmation,
  DeviceQuestion,
  PresentationQuestion,
  BoxQuestion,
  VialAlreadyOpenCard,
  DoseNotCovered,
  DoseQuestion,
  InjectionCoachWalk,
  RouteQuestion,
} from "./InjectionCoachWalk";
import { chosenDoseValue, doseQuestion } from "../doseQuestion";
import type { ProtocolDoseUnit } from "@src/packages/injection-protocol/types";
import { cameraRoleFor } from "../cameraRole";
import { semanticColors } from "@louise/theme/tokens";
import {
  choiceFromPrescribedName,
  pairingFromPrescribedName,
  type ProtocolPairing,
} from "../../injection-protocol/pairings";

const ns = "care.injection_coach";

/**
 * The coach's surfaces, mounted once at the navigator.
 *
 * They used to live inside the entry point, which put them inside whatever
 * rendered it — and on a medication row that means inside the row's own
 * `Pressable`. React Native Web propagates along the React tree rather than the
 * DOM, so a tap on "a pen" inside the device question bubbled to the row and
 * opened the medication factsheet instead of starting the walk. Stopping
 * propagation at each button would have been whack-a-mole: every new control
 * inside the sheet would need to remember.
 *
 * Hoisting them fixes that by construction, and takes two other things with it.
 * There is one instance rather than one per row — a day list with six
 * medications was mounting six consent sheets and six walk modals. And the
 * escalation the coach needs lives in the same provider as the thing that opens
 * it, so a surface can never have one without the other.
 */

export type InjectionCoachRequest = {
  /** The name on her box — used to find the protocol and to name the product. */
  medicationName: string;
  /** Her recorded dose, so no step names a number that is not hers. */
  prescribedDose?: string | null;
  /**
   * Where a dose she confirms against her box is written back.
   *
   * Supplied by a surface that owns a medication record, because the coach
   * package may not reach the API. Called with the label's number in the
   * label's unit — the button she pressed — so the record ends up right for
   * next time rather than asking her again. Absent on a reference surface, and
   * her answer then lasts for this walk only.
   */
  onCorrectDose?: (dose: { value: number; unit: ProtocolDoseUnit }) => void;
  /**
   * The scheduled dose this walk is for, when it was opened against one.
   *
   * Recorded with the session so the receipt says which dose was coached rather
   * than only that coaching happened. Absent from a factsheet opened as
   * reference, which is the same gate that decides whether finishing can record
   * anything at all.
   */
  patientMedicationId?: string | null;
  /**
   * What "I'm finished" should lead to, decided by the surface that opened the
   * coach.
   *
   * Usually the dose logger. #2267 asks that a patient not be made to finish the
   * coaching experience and then separately declare she took the medication, and
   * her own explicit confirmation in that dialog is the approved signal — trust
   * rank 2 in its completion table. So the walk hands her straight to it.
   *
   * The coach does not know what a dose is and must not: it offers no opinion on
   * whether one happened, it just stops guiding and returns her to whoever asked.
   * Absent on a surface with no dose behind it — the factsheet may be open with
   * no prescription — and finishing then simply closes.
   */
  onFinished?: () => void;
  /**
   * The device her calendar already records for this medication.
   *
   * She answered this when she added the medication — "méthode d'administration",
   * with the same five buttons the coach was about to show her again. Reading it
   * is not an optimisation: every question asked at this moment is one more
   * thing between her and a dose that is already timed, and asking one the app
   * has already been told is the kind of thing that makes a tool feel like it is
   * not listening.
   *
   * Null means the field was left empty, or holds a value no protocol describes.
   * Then she is asked, and told why.
   */
  prescribedForm?: string | null;
  /**
   * Whether a dose was already logged against **this** calendar line recently.
   *
   * Read by `care` — `useOpenedVialEvidence` — and passed in, because nothing
   * in this package may reach the API. It matters for one medication so far:
   * a Menopur 600 or 1200 IU vial is mixed once and drawn from for up to 28
   * days, so a dose already given means there is nothing left to dissolve.
   *
   * `null` is a real value and means the record could not be read, which is
   * not the same as "no dose". Only `true` skips the mixing.
   */
  hasEarlierDose?: boolean | null;
  /**
   * The container size her calendar already holds for this medication.
   *
   * Read by `care` and passed in, like every other record the coach uses:
   * nothing in this package reaches the API. A value here is what removes the
   * vial question — she is shown what is on file and agrees in one tap
   * (Anaïs, 2026-09-30).
   *
   * Absent until the API carries the field, which is dashboard-api #1905. The
   * path is wired so that landing it is a prop and not a redesign.
   */
  recordedPackageQuantity?: { value: number; unit: string } | null;

  /**
   * She corrected us, and the calendar should learn it.
   *
   * Called when the device she confirms is not the one her record names. The
   * coach does not write it itself — nothing in this package may reach the API
   * except the consent receipt, and `no-retention.test.ts` enforces that — so
   * the surface that opened the coach performs the write and this is how it
   * hears about it.
   *
   * Absent means the correction is not stored, which is the honest state for a
   * factsheet opened as reference with no prescription behind it.
   */
  onFormCorrected?: (method: string) => void;
  /**
   * She read a container size off the glass, and her calendar should learn it.
   *
   * The twin of `onFormCorrected`, and it exists for the same reason: the coach
   * does not write. It reports the size upward and the surface that knows which
   * record it is performs the write — `no-retention.test.ts` allows this package
   * no API but the consent receipt.
   *
   * Called when she answers the question — which she reaches only with nothing
   * on file, or having just refused what was there. Confirming a recorded size
   * calls nothing, because nothing changed. A write that would restate the
   * stored value is refused by the hook that performs it, where
   * `useMedicationFormCorrection` keeps the same refusal for the device.
   *
   * Absent until the API carries the field (dashboard-api #1905), and absent on
   * a factsheet opened as reference with no prescription behind it, which is
   * the honest state for both.
   */
  onPackageQuantityAnswered?: (size: { value: number; unit: string }) => void;
};

type InjectionCoachContextValue = {
  /** Whether a coach can run at all — see `onCallClinic` below. */
  available: boolean;
  open: (request: InjectionCoachRequest) => void;
  /**
   * The record landed after she opened the coach, which is the ordinary case.
   *
   * `open()` snapshots the surface's props, and the read behind
   * `hasEarlierDose` starts when that surface mounts — so a patient who taps
   * the entry point promptly carried `null` for the whole session, and `null`
   * routes her to the mixing walk. The surface pushes the answer when it
   * arrives; the host decides whether it is still welcome.
   */
  noteEarlierDose: (
    patientMedicationId: string,
    hasEarlierDose: boolean | null,
  ) => void;
  /** Reaching a human, from inside the walk. */
  callClinic: () => void;
};

const InjectionCoachContext = createContext<InjectionCoachContextValue | null>(
  null,
);

/**
 * The recorder above, the coach inside it.
 *
 * Split in two because the coach itself reads the recorder — it clears the
 * receipt when it opens and when it closes — and a component cannot consume a
 * context it renders. Mounted here rather than at the app root so the two stay
 * together: a surface that has the coach has the record of it.
 */
export function InjectionCoachProvider({
  onCallClinic,
  children,
}: {
  onCallClinic: () => void;
  children: React.ReactNode;
}) {
  return (
    <CoachRecordsProvider>
      <InjectionCoachHost onCallClinic={onCallClinic}>
        {children}
      </InjectionCoachHost>
    </CoachRecordsProvider>
  );
}

function InjectionCoachHost({
  onCallClinic,
  children,
}: {
  /**
   * How the coach reaches her care team.
   *
   * #2267 requires that any uncertain, painful or out-of-scope moment reach a
   * person, so a coaching surface that cannot escalate must not offer to guide a
   * needle. Declaring it here rather than passing it down means the escalation
   * is a property of the app, not of whichever screen remembered to supply one.
   */
  onCallClinic: () => void;
  children: ReactNode;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  const [request, setRequest] = useState<InjectionCoachRequest | null>(null);
  /**
   * Whether she has started walking, which is when the record stops being
   * allowed to change anything.
   *
   * A ref rather than state: nothing renders from it, and it is read inside a
   * callback that must not be rebuilt when it flips — the surfaces subscribe
   * to that callback's identity.
   *
   * The rule it enforces: a late record may still decide which walk she is
   * offered, and may never swap the walk she is standing in. That swap is a
   * real transition in this host — it is what her own tap on "my vial is
   * already mixed" does — and only one of the two is hers.
   */
  const walkBegun = useRef(false);

  const [stage, setStage] = useState<"disclosure" | "walking">("disclosure");
  /**
   * What she actually allowed, carried from the disclosure into the walk.
   *
   * Read from the consent sheet's answer rather than from the OS permission:
   * a granted permission she did not ask to use is not a reason to turn her
   * camera on. Reset on every open, so a previous session's choice can never
   * start a camera she has not just agreed to.
   */
  const [withCamera, setWithCamera] = useState(false);
  /**
   * The administration method she has named this session, in her own words.
   *
   * A stored value (`pre_filled_pen`, `lyophilized_powder`, …) and not a
   * translated one, so what she answers here is exactly what goes back to her
   * calendar. See `ADMINISTRATION_METHODS`.
   */
  const [form, setForm] = useState<string | null>(null);
  /**
   * Whether she has looked at what her record says and agreed with it.
   *
   * A third state, not a boolean on `form`. "The calendar says a pen" and "she
   * has confirmed it is a pen" are different facts, and only the second may
   * start a walk: her pharmacy can substitute one device for another between
   * cycles — in France, at the counter, since December 2024 — so a recorded
   * device is a good guess and never an answer.
   */
  const [formConfirmed, setFormConfirmed] = useState(false);
  /**
   * She looked at what her record says and said it is not what she is holding.
   *
   * A third state and not the absence of the second. "Not yet confirmed" is
   * what shows her the card; "declined" is what must take her past it to the
   * buttons. Setting `formConfirmed` to false on the change button was a no-op
   * — it was already false, which is why the card was on screen — so the
   * button did nothing at all.
   */
  const [formDeclined, setFormDeclined] = useState(false);
  /**
   * She has none of the devices this medication is dispensed as.
   *
   * Distinct from declining a proposal, which only says "not that one". This
   * says "none of yours", and there is nothing left to ask — she is told we
   * have no steps for what she is holding rather than being walked through
   * someone else's syringe.
   */
  const [noDeviceMatches, setNoDeviceMatches] = useState(false);
  const [route, setRoute] = useState<"intramuscular" | "subcutaneous" | null>(
    null,
  );
  /**
   * The two medications of a combined prescription, in the order they are given.
   *
   * Null until she says which combination she was prescribed — a record that
   * says "Dual Trigger" does not say whether that is Lupron with Pregnyl or
   * Lucrin with Ovitrelle, and the two are different walks. See `pairings.ts`.
   */
  const [pair, setPair] = useState<readonly [string, string] | null>(null);
  /**
   * She said her combination is not in the list.
   *
   * The answer is to say so — the pair is not in the catalogue yet — and not to
   * hand her to her care team: she asked for a guide, and "we don't have one"
   * is the true answer to that. Reaching a person is one tap away on every
   * other screen of the walk, and it is not what this question was about
   * (Anaïs, 2026-09-23).
   */
  const [pairingUnlisted, setPairingUnlisted] = useState(false);
  /** Which half of the pair is on screen: 0, then 1. */
  const [half, setHalf] = useState(0);
  /**
   * What she said her box says, when her record and the label disagreed.
   *
   * Three answers, kept apart because they lead to different places: a strength
   * she picked settles the walk with that dose; "none of these" ends it; going
   * on without a dose — the only way forward for a titrated product — settles
   * it with nothing to say. Reset on every open, like the device and the route.
   */
  const [doseAnswer, setDoseAnswer] = useState<string | null>(null);
  const [doseUnlisted, setDoseUnlisted] = useState(false);
  const [doseWithout, setDoseWithout] = useState(false);

  /**
   * The receipt belongs to the opening that produced it.
   *
   * Cleared when the coach opens and when it closes, so a walk can only be
   * recorded when a disclosure was read for *it*. Without this the id outlived
   * the sheet: the trigger-step panel, which shows no disclosure of its own,
   * would have recorded a full session against whichever consent happened to
   * be in hand from an earlier medication.
   */
  const { forgetConsent } = useCoachRecords();

  const close = useCallback(() => {
    setRequest(null);
    setStage("disclosure");
    setForm(null);
    setFormConfirmed(false);
    setFormDeclined(false);
    setNoDeviceMatches(false);
    setVialAnswer(null);
    setVialUnlisted(false);
    setBoxAnswer(null);
    setBoxUnlisted(false);
    setSaidAboutVial(null);
    setVialOpenConfirmed(false);
    walkBegun.current = false;
    setRoute(null);
    setPair(null);
    setHalf(0);
    setPairingUnlisted(false);
    setDoseAnswer(null);
    setDoseUnlisted(false);
    setDoseWithout(false);
    setWithCamera(false);
    forgetConsent();
  }, [forgetConsent]);

  /**
   * Close, then hand her on.
   *
   * That order: the walk's modal is dismissed before the next one is asked for,
   * so two sheets are never up at once and the logger is not competing with a
   * closing animation for the same screen.
   */
  const finish = useCallback(() => {
    const onFinished = request?.onFinished;
    close();
    onFinished?.();
  }, [request, close]);

  const open = useCallback((next: InjectionCoachRequest) => {
    setForm(null);
    setFormConfirmed(false);
    setFormDeclined(false);
    setNoDeviceMatches(false);
    setVialAnswer(null);
    setVialUnlisted(false);
    setBoxAnswer(null);
    setBoxUnlisted(false);
    setSaidAboutVial(null);
    setVialOpenConfirmed(false);
    walkBegun.current = false;
    setRoute(null);
    setPair(null);
    setHalf(0);
    setPairingUnlisted(false);
    setDoseAnswer(null);
    setDoseUnlisted(false);
    setDoseWithout(false);
    setStage("disclosure");
    setWithCamera(false);
    setRequest(next);
  }, []);

  /**
   * Reaching a person means leaving the coach, not navigating behind it.
   *
   * The app's escalation moves the navigator, and a `Modal` is presented above
   * the navigation container — so calling it from inside the walk changed the
   * screen underneath and showed her nothing. The button was dead at exactly the
   * moment #2267 says one must not be.
   */
  const callClinic = useCallback(() => {
    close();
    onCallClinic();
  }, [close, onCallClinic]);

  /**
   * Stable, because the walk holds it in an effect's dependency list.
   */
  const markWalkBegun = useCallback(() => {
    walkBegun.current = true;
  }, []);

  const noteEarlierDose = useCallback(
    (patientMedicationId: string, hasEarlierDose: boolean | null) => {
      if (walkBegun.current) return;
      setRequest((current) =>
        current &&
        current.patientMedicationId === patientMedicationId &&
        current.hasEarlierDose !== hasEarlierDose
          ? { ...current, hasEarlierDose }
          : current,
      );
    },
    [],
  );

  const value = useMemo<InjectionCoachContextValue>(
    () => ({ available: true, open, noteEarlierDose, callClinic }),
    [open, noteEarlierDose, callClinic],
  );

  /**
   * The prescription this walk is for, which is not always the one she opened.
   *
   * A combined trigger is two medications at one hour. She opens "Dual
   * Trigger", says which combination it is, and then walks each half in turn —
   * so from here down, everything reads the half in hand rather than the name
   * on the row.
   */
  const pairing = request
    ? pairingFromPrescribedName(request.medicationName)
    : null;

  /**
   * The device her record names, until she says otherwise.
   *
   * Held apart from `form` — the answer she has given this session — because
   * the two are different claims. `form` is what she told the coach; this is
   * what the calendar was told, possibly months ago, possibly by an import. It
   * is offered to her for confirmation rather than acted on, which is the whole
   * difference between reading her record and assuming it.
   */
  const recordedForm = pairing ? null : (request?.prescribedForm ?? null);

  /**
   * Store what she says the device is — unless the row is two medications.
   *
   * A combined trigger is one calendar item and two products, so its single
   * `form` field cannot describe both halves. Writing one half's device to it
   * replaces a fact about the other: a "Lupron + Ovidrel" row correctly
   * recorded as a prefilled syringe would be rewritten to a vial by the
   * leuprolide half, losing the Ovidrel device from her record and seeding the
   * carried-over-answer bug the half transition now resets.
   *
   * The same reasoning is why `recordedForm` is null on a pairing: a value that
   * cannot describe this half is not an answer about it, so she is asked.
   */
  const correctRecordedForm = useCallback(
    (method: string) => {
      if (pairing) return;
      request?.onFormCorrected?.(method);
    },
    [pairing, request],
  );
  /**
   * The combination her record names, where it names one.
   *
   * "Dual Trigger" says a pair and not which; "Lupron + Pregnyl" says both, and
   * asking her to choose from a list that contains what she is already looking
   * at is a tap for nothing at a timed moment.
   */
  const namedChoice =
    pairing && request
      ? choiceFromPrescribedName(pairing, request.medicationName)
      : null;
  const walks = pair ?? namedChoice?.walks ?? null;
  const walkingName = walks?.[half] ?? request?.medicationName ?? "";
  // Her country, for the handful of procedures whose steps differ by market.
  // Read here rather than inside `resolveCoachProtocols` so the dependency is
  // visible at the place that starts a walk. See `protocolsForMarket`.
  const countryCode = useCountryCode();
  // Which vial she said she has, and whether she is opening a new one whatever
  // her record implies. Session state, like her device answer: the record is a
  // presumption and she is the one who can see the glass.
  /**
   * The container size she read off the glass, in the protocol's own unit.
   *
   * A number rather than a vocabulary: it is what is printed on her vial, and
   * it is what her calendar stores once the field exists to store it. One fact
   * instead of two kept in step — the lesson the device question already
   * carries.
   */
  const [vialAnswer, setVialAnswer] = useState<number | null>(null);
  /** She has said her vial is none of the sizes offered. */
  const [vialUnlisted, setVialUnlisted] = useState(false);
  /**
   * Which box she said she has, where one medication comes as two of the same
   * device. Session-only, like the route: nothing on her record tells the two
   * apart yet. See `boxDecision`.
   */
  const [boxAnswer, setBoxAnswer] = useState<string | null>(null);
  /** She has said her box is none of the ones offered. */
  const [boxUnlisted, setBoxUnlisted] = useState(false);
  /**
   * What she has said about this vial: neither, or one of the two answers.
   *
   * One piece of state rather than two flags, because "I'm opening a new one"
   * and "mine is already mixed" answer the same question — see
   * `presentationDecision`, where holding them apart is what makes the
   * contradiction unrepresentable.
   */
  const [saidAboutVial, setSaidAboutVial] = useState<
    "already-mixed" | "new-vial" | null
  >(null);
  const [vialOpenConfirmed, setVialOpenConfirmed] = useState(false);
  const coachable = request
    ? resolveCoachProtocols(walkingName, countryCode)
    : null;
  const protocols = coachable?.protocols ?? [];
  /**
   * She answered, and the answer is not one of this medication's procedures.
   *
   * Either it translated to nothing, or it named a device no candidate has. She
   * is asked either way — guessing which powder she meant would be worse than
   * one tap — but she is told the truth about why.
   */
  const recordedButUnusable =
    Boolean(recordedForm?.trim()) && !methodIsOffered(protocols, recordedForm);

  /**
   * Narrowed on both axes, and only on the ones that can decide.
   *
   * Picking by device alone was enough while every protocol in the catalogue
   * was a device variant of one route. Urinary hCG is the opposite — one vial,
   * two routes — so device narrowing leaves two candidates and taking the first
   * would have started an intramuscular walk for a patient prescribed a
   * subcutaneous one, or the reverse. Whichever axis is still ambiguous is the
   * question she is asked; an axis all candidates agree on is never asked,
   * because it has one answer.
   */
  // Her answer if she has given one, otherwise her record's — and the record
  // only counts once she has confirmed it, which is what `formConfirmed` is
  // for. Before that it narrows nothing, so the confirmation card below is what
  // she meets rather than a walk that already decided.
  /**
   * The device question, decided by the one function both doors share.
   *
   * It was a chain of ternaries here, and the trigger-shot panel had its own
   * shorter one — same patient, same medication, two behaviours. See
   * `deviceDecision`, which is also where the behaviour is tested.
   */
  const device = deviceDecision({
    protocols,
    recordedForm,
    answered: form,
    confirmed: formConfirmed,
    declined: formDeclined,
    noneOfThese: noDeviceMatches,
  });
  const offeredMethods = methodsFor(protocols);
  const proposedMethod = device.proposed;
  const decidedForm = device.decided;

  const byForm =
    protocols.length > 1 && offeredMethods.length > 1
      ? decidedForm
        ? protocolsForMethod(protocols, decidedForm)
        : []
      : protocols;
  // Hers, on both surfaces. A recorded route was briefly trusted here and the
  // trigger-step panel does not trust one either: the field cannot say whether
  // a clinic chose it or this app's own medication presets did, and the
  // difference is which side of her body the needle goes into. See
  // `routeFromPrescription`.
  const byRoute =
    byForm.length > 1 && new Set(byForm.map((p) => p.route)).size > 1
      ? route
        ? byForm.filter((candidate) => candidate.route === route)
        : []
      : byForm;
  /**
   * Which vial, where one medication ships as two that behave differently.
   *
   * A second axis beside the device, and it exists for one medication so far.
   * Menopur's 600 and 1200 IU vials are mixed once and kept for 28 days; its 75
   * and 150 IU vials are mixed fresh each time, and the two procedures
   * contradict each other at the end. Nothing in her record tells them apart —
   * both are "powder + solvent" — so she is asked, once, right after the device
   * is confirmed. See `presentationDecision`, where it is tested.
   */
  /**
   * Which box, where her device and her route still leave two walks: Mounjaro's
   * KwikPen or its single-dose pen. Before the vial question, which narrows
   * what this leaves. See `boxDecision`, where it is tested.
   */
  const box = boxDecision({
    protocols: byRoute,
    answered: boxAnswer,
    unlisted: boxUnlisted,
  });
  const presentation = presentationDecision({
    protocols: box.chosen,
    answered: vialAnswer,
    // Read by `care` and passed in: nothing in this package reaches the API.
    // `null` means the record could not be read, which is not the same as "no
    // dose" and must never skip the mixing.
    hasEarlierDose: request?.hasEarlierDose ?? null,
    saidAboutVial,
    // Her calendar's size, and what she said when shown it. A record is a
    // presumption and never an answer — her pharmacy can dispense a different
    // container between cycles — so it is offered for confirmation.
    recorded: request?.recordedPackageQuantity ?? null,
    // Both halves, never the number alone — see `presentationDecision`.
    unlisted: vialUnlisted,
  });
  const byPresentation = presentation.chosen;
  const chosen = byPresentation.length === 1 ? byPresentation[0] : null;

  /**
   * Her way out of the mixing steps, offered only where it leads somewhere.
   *
   * The walk she is on assumes this is the first evening, and on a vial kept
   * for 28 days that assumption is wrong roughly twenty-seven times out of
   * twenty-eight for anyone whose doses are not logged. It is undefined unless
   * a sibling walk for the already-mixed vial exists for the same device and
   * market, so a procedure with nowhere to send her never shows the button.
   */
  const onVialAlreadyMixed =
    chosen?.presentation === "multidose-first" &&
    byRoute.some((candidate) => candidate.presentation === "multidose-later")
      ? () => setSaidAboutVial("already-mixed")
      : undefined;

  /**
   * Whether her record's device is worth showing her before anything starts.
   *
   * Only when it would actually decide something: a medication whose procedures
   * all share a device has nothing to confirm, and a record naming a device no
   * candidate has is not an answer at all — both fall through to the question,
   * which is the honest outcome.
   */

  /**
   * Her dose against the label's, once the procedure is known.
   *
   * Asked last, after device and route, because it needs the one protocol she
   * is holding: the unit and the strengths are the label's, and two devices of
   * one medication can be sold in different ones.
   *
   * Never on a pair. The dose recorded against a combined row is both
   * products', so it cannot agree with either label, and asking her to correct
   * it would write one half's dose over the record of two.
   */
  const dose =
    chosen && request && !walks
      ? doseQuestion({
          protocol: chosen,
          recorded: request.prescribedDose,
          answered: doseAnswer,
          unlisted: doseUnlisted,
          withoutDose: doseWithout,
        })
      : null;

  /** What is left to ask, if anything. */
  const asking:
    | "confirm-device"
    | "no-other-device"
    | "device"
    | "box"
    | "no-such-box"
    | "presentation"
    | "no-such-vial"
    | "vial-open"
    | "route"
    | "dose"
    | "dose-not-covered"
    | null =
    device.stage === "confirm"
      ? "confirm-device"
      : // Everything else the device question can answer, in one place — see
        // `deviceDecision`, where it is tested press by press.
        device.stage === "unguided"
        ? "no-other-device"
        : device.stage === "choose"
          ? "device"
          : box.stage === "ask"
            ? "box"
            : box.stage === "unguided"
              ? "no-such-box"
              : // The three the vial question can end in, read straight off the
                // decision rather than reconstructed here. A record means the
                // card, no record means the question, and she never meets both —
                // which is a property of `presentationDecision` and not of this
                // chain remembering to check.
                presentation.stage === "ask"
                ? "presentation"
                : presentation.stage === "unguided"
                  ? "no-such-vial"
                  : // Her record positively said this vial is open, and a record is a
                    // presumption: one vial finished and the next opened looks
                    // exactly like a later evening from the outside.
                    //
                    // `assumedLaterEvening`, never `!assumedFirstEvening`: the latter
                    // is false for three reasons that have nothing to do with a
                    // record, and reading it as one showed this card — which claims
                    // what her calendar says — to every patient on every medication,
                    // with nothing read.
                    presentation.assumedLaterEvening && !vialOpenConfirmed
                    ? "vial-open"
                    : chosen || protocols.length === 0
                      ? // Last, once the procedure is the one she holds: the
                        // unit and the strengths are that label's.
                        dose?.stage === "ask"
                        ? "dose"
                        : dose?.stage === "unguided"
                          ? "dose-not-covered"
                          : null
                      : byForm.length === 0
                        ? "device"
                        : "route";

  /**
   * The open question, asked before the disclosure rather than after it.
   *
   * It used to come after, and that was harmless while every protocol in the
   * catalogue was a device variant of one route: whichever device she named,
   * the camera would have done the same thing, so the disclosure could describe
   * it before knowing the answer.
   *
   * The route broke that. Urinary hCG goes into a muscle or under the skin, and
   * the camera is a mirror on one and a guide on the other — so a disclosure
   * shown first would offer her an outline on her body and then hand her a
   * mirror, which is the sheet promising something the walk does not do. The
   * question is short, it is about her prescription rather than about the
   * camera, and answering it first is what lets the offer be accurate.
   */
  const question =
    pairing && !walks ? (
      pairingUnlisted ? (
        <UnguidedHalf
          t={t}
          messageKey="this_combination_is_not_guided_yet"
          onClose={close}
        />
      ) : (
        <PairingQuestion
          t={t}
          pairing={pairing}
          onChoose={setPair}
          onNoneOfThese={() => setPairingUnlisted(true)}
        />
      )
    ) : asking === "route" ? (
      <RouteQuestion t={t} onConfirmRoute={setRoute} />
    ) : asking === "confirm-device" && proposedMethod ? (
      <DeviceConfirmation
        t={t}
        label={recordedDeviceLabel(proposedMethod, t)}
        // Two sentences, because the proposal has two sources: her own record,
        // or — where she recorded nothing and the medication comes one way —
        // the medication itself. Attributing the second to her calendar would
        // be the app quoting her on something she never said.
        // Whether the proposal came from her calendar — not whether we can
        // walk it. This asked `methodIsOffered`, which is false in exactly the
        // case the card exists for, so a patient whose record named something
        // unusual was told "this medication comes as …" about her own answer.
        fromHerRecord={recordedForm !== null}
        onlyPresentation={offeredMethods.length <= 1}
        onConfirm={() => {
          // She has confirmed a device we have no steps for. Now — and not
          // before she had the chance to correct the record — she is told.
          if (!device.proposalIsGuided) {
            setNoDeviceMatches(true);
            return;
          }
          setFormConfirmed(true);
          // Her calendar learns it, including the case where it held nothing
          // and the proposal came from the medication. She has just confirmed
          // it, which is the same signal as picking it from the buttons.
          if (proposedMethod && proposedMethod !== recordedForm) {
            correctRecordedForm(proposedMethod);
          }
        }}
        // Straight to the buttons. Her record is not treated as an answer on
        // the way — she has just said it is wrong — and whatever she picks
        // there is written back to the calendar, so the two stop disagreeing.
        onChange={() => setFormDeclined(true)}
      />
    ) : asking === "no-other-device" ? (
      <UnguidedHalf
        t={t}
        // Named rather than implied. "We don't have that one yet" claims to know
        // what she is holding, and she has just said it is something else — so
        // the sentence lists what we *do* cover and lets her match her box
        // against it (Anaïs, 2026-09-28).
        messageKey="we_only_have_steps_for_these_presentations"
        params={{ devices: deviceList(offeredMethods, t) }}
        onClose={close}
      />
    ) : asking === "box" ? (
      <BoxQuestion
        t={t}
        locale={locale === "fr" ? "fr" : "en"}
        boxes={box.options}
        onChoose={setBoxAnswer}
        // The same escape as the device and the vial: a question she cannot
        // answer truthfully needs a way out that is not a wrong button.
        onNoneOfThese={() => setBoxUnlisted(true)}
      />
    ) : asking === "no-such-box" ? (
      <UnguidedHalf
        t={t}
        // Named rather than implied, like the device and vial refusals.
        messageKey="we_only_have_steps_for_these_boxes"
        params={{
          boxes: box.options
            .map((option) => option.label[locale === "fr" ? "fr" : "en"])
            .join(", "),
        }}
        onClose={close}
      />
    ) : asking === "presentation" ? (
      <PresentationQuestion
        t={t}
        options={presentation.options}
        unit={presentation.optionUnit}
        onChoose={(size) => {
          setVialAnswer(size);
          // Her calendar learns it, the same way it learns a corrected device.
          //
          // The unit travels with the number, because the write has to store
          // the pair the read compares. A hook left to supply it on its own
          // would have to guess — her dose's unit is the obvious guess and it
          // is wrong, which is how a calendar comes to hold `600 mg` for a
          // 600 IU container, the exact state the read side refuses.
          //
          // Reported unconditionally: refusing a write that restates the
          // stored value belongs in the hook that performs it, which is where
          // `useMedicationFormCorrection` keeps the same refusal. The host
          // knowing what is already stored twice is how the two drift.
          if (presentation.optionUnit) {
            request?.onPackageQuantityAnswered?.({
              value: size,
              unit: presentation.optionUnit,
            });
          }
        }}
        // Four numbers that do not match what is in her hand, and no way past
        // them but the X — the device question has had this escape since the
        // start and this one did not (Anaïs, 2026-09-30).
        onNoneOfThese={() => setVialUnlisted(true)}
      />
    ) : asking === "no-such-vial" ? (
      <UnguidedHalf
        t={t}
        // Named rather than implied, like the device refusal: the sentence
        // lists what we do cover and lets her match her own glass against it.
        messageKey="we_only_have_steps_for_these_vial_sizes"
        params={{
          sizes: presentation.options
            .map((size) =>
              presentation.optionUnit
                ? `${size} ${presentation.optionUnit}`
                : String(size),
            )
            .join(", "),
        }}
        onClose={close}
      />
    ) : asking === "vial-open" ? (
      <VialAlreadyOpenCard
        t={t}
        onKeepUsing={() => setVialOpenConfirmed(true)}
        onStartingNew={() => setSaidAboutVial("new-vial")}
      />
    ) : asking === "device" ? (
      <DeviceQuestion
        t={t}
        // The devices these candidates come in, not a list of the ones the
        // catalogue used to contain.
        // Without the one she has just declined, where there is another. Offering
        // it back is offering her the answer she came to this card to refuse.
        methods={device.methods}
        // Said out loud only when her calendar genuinely has nothing. If she is
        // here because she corrected a recorded device, telling her it was
        // never recorded would be false.
        // Three different reasons to be on this card, and she is told which.
        // Nothing on file; something on file that no procedure here matches; or
        // she has just told us the thing on file is wrong — and that last one
        // needs no explanation, because she is the one who said it.
        unrecorded={
          recordedForm === null && !recordedButUnusable && !formDeclined
        }
        // "Your answer will be saved" is only true where there is a record to
        // save it to. A factsheet opened as reference has none.
        saved={Boolean(
          request?.onFormCorrected && request?.patientMedicationId,
        )}
        mismatched={recordedButUnusable && !formDeclined}
        onConfirmForm={(chosen) => {
          setForm(chosen);
          // The calendar learns it too, and there is nothing to translate on
          // the way: she picked from the same five values her own form writes.
          // Only when it would actually change what is stored — a write that
          // restates what is already there is a request for nothing.
          if (chosen !== recordedForm) correctRecordedForm(chosen);
        }}
        onNoneOfThese={() => setNoDeviceMatches(true)}
      />
    ) : asking === "dose" && chosen && dose ? (
      <DoseQuestion
        t={t}
        medicationName={coachable?.brandName ?? request?.medicationName ?? ""}
        question={dose}
        onConfirmDose={(answer) => {
          setDoseAnswer(answer);
          // The label's number in the label's unit, read back from the
          // protocol rather than from the button's text.
          const value = chosenDoseValue(chosen, answer);
          if (value) request?.onCorrectDose?.(value);
        }}
        onContinueWithoutDose={() => setDoseWithout(true)}
        onUnlisted={() => setDoseUnlisted(true)}
      />
    ) : asking === "dose-not-covered" && dose ? (
      <DoseNotCovered t={t} question={dose} />
    ) : null;

  return (
    /* The recorder wraps everything the coach renders — the app below it, the
       disclosure sheet and the walk — because all three have to share one
       consent id. A hook called in two components is two sets of refs, and the
       sheet's receipt was invisible to the walk that ran under it. */
    <InjectionCoachContext.Provider value={value}>
      {children}

      {/* Mounted only while open, not kept mounted and toggled.

          A React Native `Modal` is presented in mount order, so one that already
          exists sits *below* one mounted later. Keeping these permanently
          mounted put them underneath the medication factsheet — itself a
          `Modal` — and the entry point there looked dead. Closing the factsheet
          first did not help: dismissing one modal and presenting another in the
          same React commit is the case RN does not handle, and the second never
          appears.

          Mounting on demand puts them on top of whatever is already there, so
          the coach can be opened from inside another sheet without that sheet
          having to get out of the way. It also means the permission read in the
          consent sheet no longer runs for every patient at startup. */}
      {request !== null && stage === "disclosure" && question !== null ? (
        <CoachSheet onClose={close}>{question}</CoachSheet>
      ) : null}

      {request !== null && stage === "disclosure" && question === null ? (
        <InjectionCoachConsentSheet
          visible
          medicationName={request.medicationName}
          onCancel={close}
          onReady={(allowed) => {
            setWithCamera(allowed);
            setStage("walking");
          }}
          // What her camera will do on this procedure, so the choice offers
          // what she will actually get rather than what the coach usually does.
          //
          // Known by the time this renders: the questions above are asked
          // first, so the procedure is settled before the camera is described.
          // The fallback is unreachable in practice — a medication with no
          // protocol never reaches the entry point that opens this — and it
          // names the guide rather than the mirror because that is what the
          // eight claims were written about.
          cameraRole={
            chosen
              ? // Every site it could turn out to be. The site is chosen inside
                // the walk, after this sheet, so a procedure that offers a
                // thigh as well as an abdomen cannot promise the outline here —
                // and the one promise this sheet must never make is one the
                // walk does not keep. Where the sites disagree it describes the
                // mirror, which is true of both.
                (chosen.sites ?? []).some(
                  (site) => cameraRoleFor(chosen, site.id) === "mirror",
                )
                ? "mirror"
                : cameraRoleFor(chosen)
              : "guide"
          }
        />
      ) : null}

      {request !== null && stage === "walking" ? (
        <CoachSheet onClose={close}>
          {/* A question can reach this stage after all, and exactly one way:
              the second half of a combined trigger. She answered everything
              the *first* medication needed before the disclosure, and then the
              walk moved on to a second one that may need its own answer —
              urinary hCG's route, or Ovidrel's device. Rendering nothing here
              left her looking at an empty sheet between two injections. */}
          {chosen ? (
            <InjectionCoachWalk
              protocol={chosen}
              // The mixing steps are the last moment at which nothing has been
              // drawn, so they are where she can still say the vial was
              // prepared on an earlier evening. It hands her to the sibling
              // walk rather than skipping ahead in this one: that walk carries
              // the 28-day check, and this one has no reason to.
              onVialAlreadyMixed={onVialAlreadyMixed}
              // From here the procedure is the one she is standing in, and a
              // record landing late may no longer change it.
              onWalkStarted={markWalkBegun}
              brandName={coachable?.brandName ?? request?.medicationName ?? ""}
              // What the question settled on: her record when it agrees with
              // the label, her answer when it did not, nothing otherwise. The
              // walk holds the same rule itself, so nothing else can reach it.
              //
              // Not on a pair: the dose recorded against a combined row is
              // both products' ("Leuprolide 1-4 mg + hCG 1,000-2,500 IU"), and
              // Ovidrel's syringe step would assert that its syringe holds it.
              prescribedDose={walks ? undefined : (dose?.dose ?? null)}
              patientMedicationId={request?.patientMedicationId}
              withCamera={withCamera}
              // `callClinic`, not the raw prop: the walk is inside a
              // `Modal`, so navigating without dismissing it first moves
              // the screen underneath and shows her nothing. See the
              // comment on `callClinic` above — it exists for this one
              // call site, and passing `onCallClinic` here bypassed it.
              onCallClinic={callClinic}
              // Nothing is recorded from here. This surface has no scheduled
              // dose to record against, and #2267 forbids manufacturing a
              // dose event from anything less than an approved signal.
              // Only promise the confirmation where one actually follows.
              // Between the two halves the walk has not ended — the hour is
              // the same and the second injection is still to come — so it
              // must not offer to close, and above all must not offer to
              // record a dose that is half given.
              finishLabelKey={
                walks && half === 0
                  ? "next_injection"
                  : request?.onFinished
                    ? "finish_and_confirm_dose"
                    : "finish_and_close"
              }
              completionHeadline={(status) =>
                walks && half === 0 && status === "complete"
                  ? `${ns}.first_injection_done`
                  : status === "aborted"
                    ? `${ns}.stopped_for_now`
                    : `${ns}.injection_walk_complete`
              }
              onFinished={(status) => {
                if (status === "restarted") return;
                // The second half of a combined trigger, when there is one.
                // Both are given at the same hour, so finishing the first is
                // not finishing — and the disclosure she read covers both,
                // which is why this does not send her back through it.
                if (status === "finished" && walks && half === 0) {
                  setHalf(1);
                  setForm(null);
                  setRoute(null);
                  // And everything she said about the *first* medication's
                  // device. Leaving `formConfirmed` set carried her answer for
                  // the leuprolide onto the hCG: `decidedForm` read the
                  // combined row's single stored method, which describes
                  // neither half, nothing matched, and the route question
                  // re-rendered forever between two timed injections with only
                  // the X to escape.
                  setFormConfirmed(false);
                  setFormDeclined(false);
                  setNoDeviceMatches(false);
                  setVialAnswer(null);
                  setVialUnlisted(false);
                  setBoxAnswer(null);
                  setBoxUnlisted(false);
                  setSaidAboutVial(null);
                  setVialOpenConfirmed(false);
                  // The second half is a different medication, and its record
                  // has not been read yet either.
                  walkBegun.current = false;
                  return;
                }
                // Aborting is leaving, not finishing. Someone who stopped
                // mid-injection must not be handed a dialog asking her to
                // record a dose she may not have taken.
                if (status === "finished") finish();
                else close();
              }}
            />
          ) : (
            (question ?? (
              <UnguidedHalf
                t={t}
                messageKey="we_cannot_guide_this_one"
                onClose={close}
              />
            ))
          )}
        </CoachSheet>
      ) : null}
    </InjectionCoachContext.Provider>
  );
}

/**
 * The half of a pair the catalogue can no longer walk.
 *
 * Unreachable today — every combination's two halves resolve, and a test holds
 * that — but the walk between two injections is the worst place in the app to
 * render nothing, and a medication removed from the registry would do exactly
 * that. It says so and offers the way out rather than a blank sheet with an X.
 */
function UnguidedHalf({
  t,
  messageKey,
  params,
  onClose,
}: {
  t: ReturnType<typeof useTranslations>;
  /** What is not guided, said plainly. */
  messageKey: string;
  /** Values the sentence names, where it names any. */
  params?: Record<string, string>;
  onClose: () => void;
}) {
  return (
    <Card>
      {/* Read as a sentence, not as a heading. It is an answer to something she
        asked, and setting it in the weight the walk uses for titles made it
        look like an alert. */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "500",
          color: EMBER.colors.text,
          lineHeight: 22,
        }}
      >
        {t(`${ns}.${messageKey}`, params ? { params } : undefined)}
      </Text>
      <CompactActionButton
        variant="secondary"
        label={t(`${ns}.stop_for_now`)}
        onPress={onClose}
      />
    </Card>
  );
}

/**
 * Which two medications, on a prescription that names a pair.
 *
 * Asked for the same reason the route is: her record is genuinely ambiguous.
 * "Dual Trigger" is a treatment, not a product — it is Lupron with Pregnyl in
 * one country and Lucrin with Ovitrelle in another — and the two are different
 * walks. The labels are the combinations exactly as her box reads, so she is
 * matching text to text rather than recalling a molecule.
 */
function PairingQuestion({
  t,
  pairing,
  onChoose,
  onNoneOfThese,
}: {
  t: ReturnType<typeof useTranslations>;
  pairing: ProtocolPairing;
  onChoose: (walks: readonly [string, string]) => void;
  onNoneOfThese: () => void;
}) {
  return (
    <Card>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 17, fontWeight: "800", color: EMBER.colors.text }}
      >
        {t(`${ns}.which_two_did_your_clinic_prescribe`)}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: EMBER.colors.textMid, lineHeight: 21 }}
      >
        {t(`${ns}.we_will_walk_you_through_both_in_turn`)}
      </Text>
      <View style={{ gap: 10 }}>
        {pairing.choices.map((choice) => (
          <CompactActionButton
            key={choice.label}
            testID={`injection-coach-pairing-${choice.label}`}
            variant="secondary"
            label={choice.label}
            onPress={() => onChoose(choice.walks)}
          />
        ))}
      </View>

      {/* Not a dead end. The list is the combinations this app has procedures
        for, and a clinic can prescribe another pair — in which case the honest
        answer is the person who prescribed it, not a walk for something else. */}
      <CompactActionButton
        testID="injection-coach-pairing-none"
        variant="secondary"
        label={t(`${ns}.mine_is_not_here`)}
        onPress={onNoneOfThese}
      />
    </Card>
  );
}

/**
 * The bottom sheet the coach's own screens are presented in.
 *
 * Shared by the question and the walk because they are the same surface at two
 * moments, and because the alternative was two copies of this chrome drifting
 * apart. Mounted on demand rather than kept mounted and toggled — see the
 * comment in the provider: a React Native `Modal` is presented in mount order,
 * and one that already exists ends up underneath whatever sheet was mounted
 * after it.
 */
function CoachSheet({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();

  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <View
        style={{
          flex: 1,
          backgroundColor: semanticColors.overlay.scrim,
          justifyContent: "flex-end",
        }}
      >
        <View
          style={{
            backgroundColor: semanticColors.background.app,
            borderTopLeftRadius: 26,
            borderTopRightRadius: 26,
            paddingTop: 14,
            paddingHorizontal: 18,
            paddingBottom: Math.max(insets.bottom, 24),
            maxHeight: "90%",
            width: "100%",
            maxWidth: 520,
            alignSelf: "center",
          }}
        >
          <IconButton
            accessibilityLabel={t(`${ns}.stop_for_now`)}
            action={`${ns}.stop_for_now`}
            onPress={onClose}
            hitSlop={10}
            size="sm"
            pressedOpacity={1}
            style={{ alignSelf: "flex-end" }}
          >
            <X size={20} color={EMBER.colors.textLight} />
          </IconButton>
          <ScrollView contentContainerStyle={{ paddingBottom: 12 }}>
            <CoachCardSurfaceProvider value="muted">
              {children}
            </CoachCardSurfaceProvider>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

/**
 * The coach, or null outside a provider.
 *
 * Nullable on purpose: the entry point renders nothing without it, so a surface
 * that cannot escalate cannot offer to guide a needle — the rule holds
 * structurally rather than by convention.
 */
export function useInjectionCoach(): InjectionCoachContextValue | null {
  return useContext(InjectionCoachContext);
}
